/**
 * frontend/src/pages/valuation/ValuationFinancials.tsx
 *
 * VAL-UPLOAD-U1B-1 — the "Financials" step, between Company and Intake. The
 * valuator uploads statements, presses "Read figures", and sees one column per
 * fiscal year with where each figure came from. Held years and held units are
 * answered here (Confirm / Discard, Confirm / Decline). Read-only: nothing is
 * typed into the valuation and no figure is computed in the browser (U2 edits).
 *
 * Data provenance: every decision is a server call; the screen shows what the
 * server holds after each one. No browser storage.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  extractFinancials,
  decideTieout,
  fetchFinancials,
  uploadFinancialsDocument,
  type ExtractBody,
  type FinancialsView,
} from '@/api/valuationFinancials'
import { formatApiError } from '@/utils/httpErrors'
import { ValuationStepper, nextStepPath } from './_shared'
import { FinancialsUploader } from './_FinancialsUploader'
import { FinancialsYearTable } from './_FinancialsYearTable'
import { FinancialsNotFilled } from './_FinancialsNotFilled'
import { FinancialsTieOut } from './_FinancialsTieOut'

const BTN = 'rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50'
const PRIMARY = `${BTN} bg-blue-700 text-white hover:bg-blue-800`
const SECONDARY = `${BTN} border border-gray-300 bg-white text-gray-800 hover:bg-gray-50`

export default function ValuationFinancials() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const [view, setView] = useState<FinancialsView | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [aiUsed, setAiUsed] = useState(false)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)

  const reload = useCallback(async (id: string) => {
    setView(await fetchFinancials(id))
  }, [])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    fetchFinancials(valuationId)
      .then(v => { if (live) setView(v) })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  /** One server call at a time; a second click while one is running does nothing. */
  async function run(work: (id: string) => Promise<void>) {
    if (!valuationId || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      await work(valuationId)
    } catch (err: unknown) {
      const message = formatApiError(err)
      setError(message)
      toast.error(message)
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const read = (body: ExtractBody = {}) =>
    run(async id => {
      const res = await extractFinancials(id, body)
      setAiUsed(res.ai_used === true)
      setNotice(res.message ?? null)
      await reload(id)
    })

  const upload = (files: File[]) =>
    run(async id => {
      for (const f of files) await uploadFinancialsDocument(id, f)
      setNotice(null)
      await reload(id)
    })

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</div>
      </div>
    )
  }
  if (!view) {
    return <div className="mx-auto max-w-7xl p-6 text-sm text-gray-500">Loading financials…</div>
  }

  const heldYears = view.last_run?.held_period ?? []
  const heldScales = view.last_run?.held_scale ?? []
  const unmapped = view.last_run?.unmapped ?? []
  const problems = view.last_run?.errors ?? []
  const hasDocs = view.documents.length > 0
  const hasRows = view.rows.length > 0

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Financials</h1>
        <p className="mt-1 text-sm text-gray-500">
          Upload the statements, read the figures, and check where each one came from. Nothing here changes the number.
        </p>
      </div>
      <ValuationStepper current="financials" engagementId={valuationId} />

      {!hasDocs && <p className="text-base font-medium text-gray-900">Upload your financial statements to get started.</p>}
      <FinancialsUploader busy={busy} onFiles={f => void upload(f)} onRefused={m => setError(m)} />
      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {hasDocs && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-900">Files</h2>
          <ul className="text-sm text-gray-700">
            {view.documents.map(d => <li key={d.doc_id}>{d.filename}</li>)}
          </ul>
          <button type="button" className={PRIMARY} disabled={busy} onClick={() => void read()}>Read figures</button>
          {aiUsed && <p className="text-xs text-gray-500">AI read this file to find some of the figures.</p>}
          {notice && <p className="text-sm text-amber-800">{notice}</p>}
        </section>
      )}

      {heldYears.map(h => (
        <div key={`${h.filename}-${h.year}`} className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
          <p className="font-medium text-gray-900">FY{h.year} from {h.filename} is on hold.</p>
          <p className="text-gray-700">{h.reason}</p>
          <div className="flex gap-2">
            <button type="button" className={PRIMARY} disabled={busy} aria-label={`Confirm FY${h.year}`}
              onClick={() => void read({ confirm_years: [h.year] })}>Confirm</button>
            <button type="button" className={SECONDARY} disabled={busy} aria-label={`Discard FY${h.year}`}
              onClick={() => void read({ discard_years: [h.year] })}>Discard</button>
          </div>
        </div>
      ))}

      {heldScales.map(s => (
        <div key={s.doc_id} className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
          <p className="font-medium text-gray-900">Are the figures in {s.filename} printed in {s.scale_name ?? 'thousands'}?</p>
          {s.evidence && <p className="text-gray-700">{s.evidence}</p>}
          <p className="text-gray-500">None of this file&apos;s figures are in the table until you answer.</p>
          <div className="flex gap-2">
            <button type="button" className={PRIMARY} disabled={busy} aria-label={`Confirm scale for ${s.filename}`}
              onClick={() => void read({ confirm_scale: [s.doc_id] })}>Confirm</button>
            <button type="button" className={SECONDARY} disabled={busy} aria-label={`Decline scale for ${s.filename}`}
              onClick={() => void read({ decline_scale: [s.doc_id] })}>Decline</button>
          </div>
        </div>
      ))}

      <FinancialsNotFilled report={view.last_run} />

      {hasRows && <FinancialsYearTable rows={view.rows} provenance={view.provenance} />}

      {(view.tieout ?? []).map(y => (
        <FinancialsTieOut key={y.year} data={y}
          onDecide={b => void run(async id => { await decideTieout(id, b); await reload(id) })} />
      ))}

      {unmapped.length > 0 && (
        <section className="space-y-1">
          <h2 className="text-sm font-semibold text-gray-900">Lines that could not be placed</h2>
          <ul className="text-sm text-gray-700">
            {unmapped.map((u, i) => (
              <li key={i}>{u.source_label || '(no label)'}{typeof u.value === 'number' ? ` — ${u.value.toLocaleString('en-US')}` : ''} · {u.filename} · {u.reason}</li>
            ))}
          </ul>
        </section>
      )}
      {problems.length > 0 && (
        <ul className="text-sm text-red-800">
          {problems.map((p, i) => <li key={i}>{p.filename}: {p.error}</li>)}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <Link
          to={nextStepPath('financials', valuationId) ?? '/valuation'}
          className="rounded bg-[#0d1f3c] px-4 py-2 text-sm font-medium text-white"
        >
          Continue to Review and adjust
        </Link>
      </div>
    </div>
  )
}
